import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router-dom";

import { Table, Modal, Button, Radio, message, Space, Spin, Input } from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  RightOutlined,
  LeftOutlined,
} from "@ant-design/icons";

import "./index.css";

export default ({
  counteragents,
  deleteCounteragent,
  profile,
  getCounteragentPayments,
  getCounteragentOrders,
  getCounteragentReceiptOrders,
  getCounteragentShifts,
  setSelectedCounteragent,
  selectedCounteragent,
  setCounteragentOrders,
  setCounteragentReceiptOrders,
  setCounteragentShifts,
  setCounteragentPayments,
  getInitialCounteragentPayments,
  getInitialCounteragentOrders,
  getInitialCounteragentReceiptOrders,
  getInitialCounteragentShifts,
}) => {
  let history = useHistory();
  let { filter: routeFilter } = useParams();
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [filter, setFilter] = useState(routeFilter);
  const [searchValue, setSearchValue] = useState("");
  const [dataSource, setDataSource] = useState([]);

  function comeback() {
    history.push("/counteragents");
  }

  const validFilters = [
    "all",
    "employees",
    "buyers",
    "suppliers",
    "positive",
    "negative",
  ];

  if (!validFilters.includes(routeFilter)) {
    routeFilter = "all";
  }

  const handleFilterChange = (e) => {
    const value = e.target.value;
    setFilter(value);
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handleEditCounteragent = async (counteragent) => {
    history.push({
      pathname: `/counteragents/edit`,
      state: {
        counteragent,
        listViewState: { filter, isModalVisible, searchValue },
        from: history.location.pathname,
      },
    });
  };

  const handleShowCounteragentPayments = async (counteragent) => {
    getInitialCounteragentPayments(counteragent);
    getCounteragentPayments(counteragent);
    history.push({
      pathname: `/counteragents/get/payments`,
      state: {
        listViewState: { filter, isModalVisible, searchValue },
        from: history.location.pathname,
      },
    });
  };

  const handleShowCounteragentOrders = (counteragent) => {
    getInitialCounteragentOrders(counteragent);
    getCounteragentOrders(counteragent);
    history.push({
      pathname: `/counteragents/get/orders`,
      state: {
        listViewState: { filter, isModalVisible, searchValue },
        from: history.location.pathname,
      },
    });
  };

  const handleShowCounteragentReceiptOrders = (counteragent) => {
    getInitialCounteragentReceiptOrders(counteragent);
    getCounteragentReceiptOrders(counteragent);
    history.push({
      pathname: `/counteragents/get/receiptorders`,
      state: {
        listViewState: { filter, isModalVisible, searchValue },
        from: history.location.pathname,
      },
    });
  };

  const handleShowCounteragentShifts = (counteragent) => {
    getInitialCounteragentShifts(counteragent);
    getCounteragentShifts(counteragent);
    history.push({
      pathname: `/counteragents/get/shifts`,
      state: {
        listViewState: { filter, isModalVisible, searchValue },
        from: history.location.pathname,
      },
    });
  };

  const handleDeleteCounteragent = async (values) => {
    setLoading(true);
    values.creatorId = profile._id;
    var result = await deleteCounteragent(values);

    if (result.success) {
      message.success("Counteragent successfully deleted!");
      setLoading(false);
    } else {
      message.error(result.message || "Failed to delete!");
      setLoading(false);
    }
  };

  const showConfirm = (category) => {
    Modal.confirm({
      title: "Are you sure you want to delete this counteragent?",
      icon: <ExclamationCircleOutlined />,
      content: "This action cannot be undone.",
      okText: "Yes, delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk() {
        handleDeleteCounteragent(category);
      },
      onCancel() {
        console.log("Cancelled");
      },
    });
  };

  const showModal = (counteragent) => {
    setSelectedCounteragent(counteragent);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setCounteragentOrders(false);
    setCounteragentReceiptOrders(false);
    setCounteragentShifts(false);
    setCounteragentPayments(false);
    setIsModalVisible(false);
  };

  const filterCounteragents = (filter, counteragents, searchValue) => {
    let filteredData = counteragents;

    switch (filter) {
      case "buyers":
        filteredData = filteredData.filter((c) => c.type === "buyer");
        break;
      case "suppliers":
        filteredData = filteredData.filter((c) => c.type === "supplier");
        break;
      case "employees":
        filteredData = filteredData.filter(
          (c) => c.type === "employee" || c.type === "workshop worker"
        );
        break;
      case "workers":
        filteredData = filteredData.filter((c) => c.type === "workshop worker");
        break;
      case "positive":
        filteredData = filteredData.filter((c) => c.balance > 0);
        break;
      case "negative":
        filteredData = filteredData.filter((c) => c.balance < 0);
        break;
      default:
        break;
    }

    if (searchValue) {
      filteredData = filteredData.filter(
        (c) =>
          c.firstname?.toLowerCase().includes(searchValue.toLowerCase()) ||
          c.lastname?.toLowerCase().includes(searchValue.toLowerCase()) ||
          (c.additionalName &&
            c.additionalName
              .toLowerCase()
              .includes(searchValue.toLowerCase())) ||
          (c.firm && c.firm.toLowerCase().includes(searchValue.toLowerCase()))
      );
    }

    return filteredData;
  };

  const prepareDataSource = (filteredData) => {
    return filteredData.map((counteragent) => ({
      key: counteragent._id,
      nameInfo: `${counteragent.firm || ""} ${counteragent.lastname || ""} ${
        counteragent.firstname || ""
      } ${counteragent.additionalName || ""}`,
      balance: counteragent.balance,
      ...counteragent,
    }));
  };

  const columns = [
    {
      title: <span className="column-title-font">Name Info</span>,
      dataIndex: "nameInfo",
      key: "nameInfo",
      render: (text) => <span className="column-content-font">{text}</span>,
    },
    {
      title: <span className="column-title-font">Balance</span>,
      dataIndex: "balance",
      key: "balance",
      render: (text) => (
        <span className="column-content-font">{text} rub.</span>
      ),
    },
    {
      title: <span className="column-title-font">Actions</span>,
      dataIndex: "details",
      key: "details",
      render: (_, record) => (
        <Space>
          <Button
            className="common-table-button"
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => showModal(record)}
          />
          <Button
            className="common-table-button"
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditCounteragent(record)}
          />
          <Button
            className="common-table-button"
            type="link"
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => showConfirm(record)}
          />
        </Space>
      ),
    },
  ];

  useEffect(() => {
    if (!counteragents || counteragents.length === 0) {
      setLoading(true);
      return;
    }

    const filteredCounteragents = filterCounteragents(
      filter,
      counteragents,
      searchValue
    );

    const dataSource = prepareDataSource(filteredCounteragents);
    setDataSource(dataSource);

    setLoading(false);
  }, [filter, counteragents, searchValue]);
  useEffect(() => {
    if (history.location.state?.listViewState) {
      const { filter, isModalVisible, searchValue } =
        history.location.state.listViewState;
      setFilter(filter);
      setIsModalVisible(isModalVisible);
      setSearchValue(searchValue);
    }
  }, [history.location.state]);

  return (
    <div className="common-container">
      <Spin spinning={loading}>
        <Button type="ghost" onClick={comeback} className="back-route-button">
          <LeftOutlined />
          <h4 className="back-route-button--title">back</h4>
        </Button>

        <br />

        <h4 className="common-title--large">All Counteragents</h4>
        <Input.Search
          value={searchValue}
          placeholder="Search by name or company"
          onChange={handleSearchChange}
          style={{ marginBottom: 16 }}
          size="small"
        />
        <Radio.Group
          onChange={handleFilterChange}
          value={filter}
          style={{ marginBottom: 16 }}
          size="small"
        >
          <Radio.Button value="all">All</Radio.Button>
          <Radio.Button value="buyers">Buyers</Radio.Button>
          <Radio.Button value="suppliers">Suppliers</Radio.Button>
          <Radio.Button value="employees">All Employees</Radio.Button>
          <Radio.Button value="workers">Workshop Workers</Radio.Button>
          <Radio.Button value="positive">Balance above 0</Radio.Button>
          <Radio.Button value="negative">Balance below 0</Radio.Button>
        </Radio.Group>
        <Table
          pagination={{ pageSize: 20 }}
          dataSource={dataSource}
          columns={columns}
        />
        <Modal
          title="Counteragent Information"
          open={isModalVisible}
          onCancel={handleCancel}
          footer={null}
        >
          {selectedCounteragent && (
            <div className="common-modal__info-block">
              {selectedCounteragent.firm && (
                <div className="counteragent-info-container">
                  <span className="column-title-font">Company:</span>
                  <span className="column-content-font">
                    {selectedCounteragent.firm}
                  </span>
                </div>
              )}
              {selectedCounteragent.firstname && (
                <div className="counteragent-info-container">
                  <span className="column-title-font">First Name:</span>
                  <span className="column-content-font">
                    {selectedCounteragent.firstname}
                  </span>
                </div>
              )}
              {selectedCounteragent.lastname && (
                <div className="counteragent-info-container">
                  <span className="column-title-font">Last Name:</span>
                  <span className="column-content-font">
                    {selectedCounteragent.lastname}
                  </span>
                </div>
              )}
              {selectedCounteragent.additionalName && (
                <div className="counteragent-info-container">
                  <span className="column-title-font">Middle Name:</span>
                  <span className="column-content-font">
                    {selectedCounteragent.additionalName}
                  </span>
                </div>
              )}

              {selectedCounteragent.phone && (
                <div className="counteragent-info-container">
                  <span className="column-title-font">Phone:</span>
                  <span className="column-content-font">
                    {selectedCounteragent.phone}
                  </span>
                </div>
              )}

              {selectedCounteragent.email && (
                <div className="counteragent-info-container">
                  <span className="column-title-font">Email:</span>
                  <span className="column-content-font">
                    {selectedCounteragent.email}
                  </span>
                </div>
              )}
              {selectedCounteragent.address && (
                <div className="counteragent-info-container">
                  <span className="column-title-font">Address:</span>
                  <span className="column-content-font">
                    {selectedCounteragent.address}
                  </span>
                </div>
              )}
              {selectedCounteragent.comment && (
                <div className="counteragent-info-container">
                  <span className="column-title-font">Comment:</span>
                  <span className="column-content-font">
                    {selectedCounteragent.comment}
                  </span>
                </div>
              )}

              <div className="counteragent-info-container">
                <span className="column-title-font">Type:</span>
                <span className="column-content-font">
                  {selectedCounteragent.type}
                </span>
              </div>
              {selectedCounteragent.profession && (
                <div className="counteragent-info-container">
                  <span className="column-title-font">Profession:</span>
                  <span className="column-content-font">
                    {selectedCounteragent.profession}
                  </span>
                </div>
              )}
              <div className="counteragent-info-container">
                <span className="column-title-font">Balance:</span>
                <span className="column-content-font">
                  {selectedCounteragent.balance} rub.
                </span>
              </div>

              <Button
                type="text"
                size="large"
                onClick={() => {
                  handleShowCounteragentPayments(selectedCounteragent);
                }}
              >
                <div className="link__title__container--counteragent-modal">
                  <p className="link__title--counteragent-modal">
                    All counteragent payments
                  </p>
                  <RightOutlined
                    style={{
                      fontSize: "10px",
                    }}
                  />
                </div>
              </Button>
              <Button
                type="text"
                size="large"
                onClick={() => {
                  handleShowCounteragentOrders(selectedCounteragent);
                }}
              >
                <div className="link__title__container--counteragent-modal">
                  <p className="link__title--counteragent-modal">
                    All counteragent orders
                  </p>
                  <RightOutlined
                    style={{
                      fontSize: "10px",
                    }}
                  />
                </div>
              </Button>

              <Button
                type="text"
                size="large"
                onClick={() => {
                  handleShowCounteragentReceiptOrders(selectedCounteragent);
                }}
              >
                <div className="link__title__container--counteragent-modal">
                  <p className="link__title--counteragent-modal">
                    All counteragent receipt orders
                  </p>
                  <RightOutlined
                    style={{
                      fontSize: "10px",
                    }}
                  />
                </div>
              </Button>

              <Button
                type="text"
                size="large"
                onClick={() => {
                  handleShowCounteragentShifts(selectedCounteragent);
                }}
              >
                <div className="link__title__container--counteragent-modal">
                  <p className="link__title--counteragent-modal">
                    All counteragent shifts
                  </p>
                  <RightOutlined
                    style={{
                      fontSize: "10px",
                    }}
                  />
                </div>
              </Button>
            </div>
          )}
        </Modal>
      </Spin>
    </div>
  );
};
